import { useState } from 'react';
import { formatDate } from '../../dates.ts';
import type { ContactLink } from '../../api/contacts.ts';
import { ContactSheet } from './ContactSheet.tsx';

/**
 * A list of contact links: name, relationship or role, the phone as a tel: link on a tablet, and the
 * flags in WORDS (Emergency, Primary, Has authority, Do not contact, Court appointed), never colour
 * alone. Each line opens the contact. Ended links sit under "Former", collapsed, each with why it ended.
 */
export function ContactLinkList({ links, empty }: { links: ContactLink[]; empty: string }) {
  const [open, setOpen] = useState<{ contact: string | null; link: string } | null>(null);
  const [former, setFormer] = useState(false);
  const current = links.filter((l) => l.is_current);
  const ended = links.filter((l) => !l.is_current);
  return (
    <>
      {current.length === 0 ? (
        <p className="text-neutral-600">{empty}</p>
      ) : (
        <ul className="divide-y divide-line">
          {current.map((l) => <Line key={l.link_id} l={l} onOpen={() => setOpen({ contact: l.contact_id, link: l.link_id })} />)}
        </ul>
      )}
      {ended.length > 0 && (
        <div className="mt-1">
          <button type="button" onClick={() => setFormer(!former)} aria-expanded={former}
                  className="min-h-[44px] text-sm font-semibold text-neutral-700 underline underline-offset-2">
            {former ? 'Hide former' : `Former (${ended.length})`}
          </button>
          {former && (
            <ul className="divide-y divide-line">
              {ended.map((l) => <Line key={l.link_id} l={l} onOpen={() => setOpen({ contact: l.contact_id, link: l.link_id })} />)}
            </ul>
          )}
        </div>
      )}
      {open && <ContactSheet contactId={open.contact} linkId={open.link} onClose={() => setOpen(null)} />}
    </>
  );
}

export function Flags({ l }: { l: Pick<ContactLink, 'do_not_contact' | 'is_emergency' | 'is_primary' | 'has_authority' | 'court_appointed'> }) {
  const tags = [
    l.do_not_contact ? 'Do not contact' : null,
    l.is_emergency ? 'Emergency' : null,
    l.is_primary ? 'Primary' : null,
    l.has_authority ? 'Has authority' : null,
    l.court_appointed ? 'Court appointed' : null,
  ].filter((t): t is string => t !== null);
  if (tags.length === 0) return null;
  return (
    <span className="ml-1 inline-flex flex-wrap gap-1 align-middle">
      {tags.map((t) => (
        <span key={t}
              className={`rounded px-1.5 py-0.5 text-xs font-semibold ${t === 'Do not contact'
                ? 'border border-warning-edge bg-warning-bg text-warning-fg' : 'border border-line bg-tint text-neutral-800'}`}>
          {t}
        </span>
      ))}
    </span>
  );
}

function Line({ l, onOpen }: { l: ContactLink; onOpen: () => void }) {
  const who = l.relationship ? `${l.relationship} · ${l.role_label}` : l.role_label;
  const endReason = l.end_reason_label;
  return (
    <li className="py-1.5">
      <button type="button" onClick={onOpen} className="min-h-[44px] w-full text-left">
        <span className="font-medium text-neutral-900">{l.name ?? 'Name not recorded'}</span>
        {l.credentials && <span className="text-neutral-700">, {l.credentials}</span>}
        <Flags l={l} />
        <span className="block text-neutral-600">
          {[who, l.organization && l.organization !== l.name ? l.organization : null].filter(Boolean).join(' · ')}
        </span>
        {!l.is_current && (
          <span className="block text-sm text-neutral-600">
            Ended {l.ended_on ? formatDate(l.ended_on) : ''}{endReason ? ` · ${endReason}` : ''}{l.end_reason_text ? `: ${l.end_reason_text}` : ''}
          </span>
        )}
      </button>
      {l.is_current && (l.phone || l.email) && (
        <span className="block text-neutral-700">
          {l.phone && <a href={`tel:${l.phone}`} className="inline-flex min-h-[44px] items-center underline">{l.phone}</a>}
          {l.phone && l.email && ' · '}
          {l.email && <a href={`mailto:${l.email}`} className="inline-flex min-h-[44px] items-center underline">{l.email}</a>}
        </span>
      )}
    </li>
  );
}
